import { useQuery } from '@tanstack/react-query'
import { createFileRoute, Link } from '@tanstack/react-router'
import { Coins, ExternalLink, Search, SlidersHorizontal, X } from 'lucide-react'
import { useState } from 'react'
import { toast } from 'sonner'

import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { Progress } from '@/components/ui/progress'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Skeleton } from '@/components/ui/skeleton'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import {
  Count,
  EmptyState,
  Page,
  PageHeader,
  Pager,
  SortableHead,
  TableCard,
  Toolbar,
} from '@/components/shell'
import { ExportButton } from '@/components/export-button'
import { TokenActions } from '@/features/tokens/token-actions'
import { api, type Paginated, type TokenRow } from '@/lib/api'
import {
  explorerTokenUrl,
  formatDateTime,
  formatPrice,
  hideCategoryLabel,
  progressPercent,
  shortAddress,
} from '@/lib/format'

export const Route = createFileRoute('/dashboard/tokens/')({
  component: TokensPage,
})

function TokensPage() {
  const [page, setPage] = useState(1)
  const [search, setSearch] = useState('')
  const [status, setStatus] = useState('all')
  const [flag, setFlag] = useState('all')
  const [sort, setSort] = useState<string | undefined>()
  const [creator, setCreator] = useState('')
  const [createdFrom, setCreatedFrom] = useState('')
  const [createdTo, setCreatedTo] = useState('')

  const advancedCount = [creator, createdFrom, createdTo].filter(Boolean).length

  const { data, isLoading } = useQuery({
    queryKey: ['tokens', { page, search, status, flag, sort, creator, createdFrom, createdTo }],
    queryFn: () =>
      api.get<Paginated<TokenRow>>('/tokens', {
        page,
        perPage: 20,
        search: search || undefined,
        status: status === 'all' ? undefined : status,
        flag: flag === 'all' ? undefined : flag,
        sort,
        creator: creator || undefined,
        createdFrom: createdFrom || undefined,
        createdTo: createdTo ? `${createdTo}T23:59:59` : undefined,
      }),
  })

  const reset = (fn: () => void) => () => {
    fn()
    setPage(1)
  }

  return (
    <Page>
      <PageHeader
        title="代币"
        description="下架违规代币、标记热门。所有写操作都会记入审计流水，且需要填写理由。"
        actions={
          <ExportButton
            filename="代币"
            fetchPage={(p, pp) =>
              api.get<Paginated<TokenRow>>('/tokens', {
                page: p,
                perPage: pp,
                search: search || undefined,
                status: status === 'all' ? undefined : status,
                flag: flag === 'all' ? undefined : flag,
                sort,
                creator: creator || undefined,
                createdFrom: createdFrom || undefined,
                createdTo: createdTo ? `${createdTo}T23:59:59` : undefined,
              })
            }
            columns={[
              { header: '符号', value: (t) => t.symbol },
              { header: '名称', value: (t) => t.name },
              { header: '合约地址', value: (t) => t.tokenAddress },
              { header: '发行人', value: (t) => t.creator },
              { header: '计价币', value: (t) => t.quoteSymbol ?? t.quote },
              { header: '状态', value: (t) => (t.hidden ? '已下架' : t.graduated ? '已毕业' : '交易中') },
              { header: '下架类型', value: (t) => hideCategoryLabel(t.hiddenCategory) },
              { header: '下架原因', value: (t) => t.hiddenReason },
              { header: '热门', value: (t) => (t.hotActive ? '是' : '否') },
              { header: '募资进度%', value: (t) => progressPercent(t.collected, t.graduation).toFixed(2) },
              { header: '成交笔数', value: (t) => t.tradeCount },
              {
                header: '累计交易额USD',
                value: (t) => (t.volAllUsd != null ? t.volAllUsd.toFixed(2) : ''),
              },
              { header: '创建时间', value: (t) => formatDateTime(t.createdTime) },
            ]}
          />
        }
      />

      <Toolbar trailing={<Count value={data?.total} />}>
        <div className="relative">
          <Search className="absolute left-3 top-1/2 size-3.5 -translate-y-1/2 text-mut2" />
          <Input
            value={search}
            onChange={(e) => reset(() => setSearch(e.target.value))()}
            placeholder="符号 / 名称 / 合约地址"
            className="h-9 w-64 rounded-apple pl-9 text-[13px]"
          />
        </div>

        <Select value={status} onValueChange={(v) => reset(() => setStatus(v))()}>
          <SelectTrigger className="h-9 w-32 rounded-apple text-[13px]">
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="rounded-apple">
            <SelectItem value="all">全部状态</SelectItem>
            <SelectItem value="live">交易中</SelectItem>
            <SelectItem value="graduated">已毕业</SelectItem>
            <SelectItem value="delisted">已下架</SelectItem>
          </SelectContent>
        </Select>

        <Select value={flag} onValueChange={(v) => reset(() => setFlag(v))()}>
          <SelectTrigger className="h-9 w-28 rounded-apple text-[13px]">
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="rounded-apple">
            <SelectItem value="all">全部标记</SelectItem>
            <SelectItem value="hot">热门</SelectItem>
            <SelectItem value="none">无标记</SelectItem>
          </SelectContent>
        </Select>

        {/* 发行人与时间区间放进浮层：常用筛选留在外面，避免工具栏过长 */}
        <Popover>
          <PopoverTrigger asChild>
            <Button
              variant="outline"
              size="sm"
              className="pressable h-9 gap-1.5 rounded-apple text-[13px]"
            >
              <SlidersHorizontal className="size-3.5" />
              更多筛选
              {advancedCount > 0 && (
                <span className="ml-0.5 flex size-4 items-center justify-center rounded-full bg-cyan/20 font-mono text-[10px] text-cyan">
                  {advancedCount}
                </span>
              )}
            </Button>
          </PopoverTrigger>
          <PopoverContent align="start" className="w-80 rounded-apple-lg p-4">
            <div className="space-y-3.5">
              <div className="space-y-1.5">
                <Label className="text-[12px]">发行人地址</Label>
                <Input
                  value={creator}
                  onChange={(e) => reset(() => setCreator(e.target.value))()}
                  placeholder="0x…"
                  className="h-8 rounded-apple-sm font-mono text-[12px]"
                />
              </div>
              <div className="grid grid-cols-2 gap-2">
                <div className="space-y-1.5">
                  <Label className="text-[12px]">创建时间自</Label>
                  <Input
                    type="date"
                    value={createdFrom}
                    onChange={(e) => reset(() => setCreatedFrom(e.target.value))()}
                    className="h-8 rounded-apple-sm text-[12px]"
                  />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-[12px]">至</Label>
                  <Input
                    type="date"
                    value={createdTo}
                    onChange={(e) => reset(() => setCreatedTo(e.target.value))()}
                    className="h-8 rounded-apple-sm text-[12px]"
                  />
                </div>
              </div>
              {advancedCount > 0 && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="pressable h-7 w-full rounded-apple-sm text-[12px]"
                  onClick={reset(() => {
                    setCreator('')
                    setCreatedFrom('')
                    setCreatedTo('')
                  })}
                >
                  <X className="size-3" />
                  清除
                </Button>
              )}
            </div>
          </PopoverContent>
        </Popover>
      </Toolbar>

      <TableCard>
        <Table>
          <TableHeader>
            <TableRow className="border-line/50 hover:bg-transparent">
              <SortableHead field="symbol" sort={sort} onSort={setSort} className="pl-6">
                代币
              </SortableHead>
              <TableHead className="section-label">合约</TableHead>
              <TableHead className="section-label">发行人</TableHead>
              <SortableHead field="progress" sort={sort} onSort={setSort}>
                进度
              </SortableHead>
              <SortableHead field="lastPrice" sort={sort} onSort={setSort} align="right">
                最新价
              </SortableHead>
              <SortableHead field="tradeCount" sort={sort} onSort={setSort} align="right">
                成交
              </SortableHead>
              <SortableHead field="volAllUsd" sort={sort} onSort={setSort} align="right">
                累计交易额
              </SortableHead>
              <TableHead className="section-label">状态</TableHead>
              <SortableHead field="createdTime" sort={sort} onSort={setSort}>
                创建时间
              </SortableHead>
              <TableHead className="section-label pr-6 text-right">操作</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading &&
              Array.from({ length: 8 }, (_, i) => (
                <TableRow key={i} className="border-line/40">
                  <TableCell colSpan={10} className="px-6 py-3">
                    <Skeleton className="h-8 w-full rounded-apple-sm" />
                  </TableCell>
                </TableRow>
              ))}

            {data?.data.length === 0 && (
              <TableRow className="hover:bg-transparent">
                <TableCell colSpan={10}>
                  <EmptyState
                    icon={Coins}
                    title="没有符合条件的代币"
                    description="调整筛选条件后再试。"
                  />
                </TableCell>
              </TableRow>
            )}

            {data?.data.map((t) => {
              const pct = progressPercent(t.collected, t.graduation)

              return (
                <TableRow
                  key={t.tokenAddress}
                  className={`row-hover border-line/40 ${t.hidden ? 'opacity-55' : ''}`}
                >
                  <TableCell className="pl-6">
                    <Link
                      to="/dashboard/tokens/$address"
                      params={{ address: t.tokenAddress }}
                      className="flex items-center gap-2.5"
                    >
                      {/* logo 来自 LaunchMeta.image，之前查出来了却没渲染 */}
                      {t.image ? (
                        <img
                          src={t.image}
                          alt=""
                          className="size-8 shrink-0 rounded-apple-sm border border-line/50 object-cover"
                        />
                      ) : (
                        <div className="flex size-8 shrink-0 items-center justify-center rounded-apple-sm border border-line/70 bg-void font-mono text-[10px] text-mut2">
                          {t.symbol.slice(0, 2)}
                        </div>
                      )}
                      <div className="min-w-0">
                        <div className="flex items-center gap-1.5">
                          <span className="text-[13.5px] text-txt">{t.symbol}</span>
                          {/* hot 用 rose——green 只给链上核验过的事实 */}
                          {t.hotActive && (
                            <Badge className="h-4 bg-rose/15 px-1.5 text-[10px] text-rose hover:bg-rose/15">
                              热门
                            </Badge>
                          )}
                        </div>
                        <div className="truncate text-[11.5px] text-mut2">{t.name}</div>
                      </div>
                    </Link>
                  </TableCell>

                  <TableCell>
                    <div className="flex items-center gap-1.5">
                      <button
                        className="pressable font-mono text-[11.5px] text-mut hover:text-cyan"
                        onClick={() => {
                          navigator.clipboard.writeText(t.tokenAddress)
                          toast.success('合约地址已复制')
                        }}
                        title={t.tokenAddress}
                      >
                        {shortAddress(t.tokenAddress)}
                      </button>
                      <a
                        href={explorerTokenUrl(t.tokenAddress)}
                        target="_blank"
                        rel="noreferrer"
                        title="在区块浏览器中查看"
                        className="text-dim hover:text-cyan"
                      >
                        <ExternalLink className="size-3" />
                      </a>
                    </div>
                  </TableCell>

                  <TableCell>
                    <Link
                      to="/dashboard/wallets"
                      search={{ address: t.creator }}
                      className="font-mono text-[11.5px] text-mut hover:text-cyan"
                      title={t.creator}
                    >
                      {shortAddress(t.creator)}
                    </Link>
                  </TableCell>

                  <TableCell className="w-28">
                    {t.graduated ? (
                      <span className="font-mono text-[11.5px] text-green">已毕业</span>
                    ) : (
                      <div className="space-y-1">
                        <Progress value={pct} className="h-1" />
                        <span className="tnum font-mono text-[10.5px] text-mut2">
                          {pct.toFixed(1)}%
                        </span>
                      </div>
                    )}
                  </TableCell>

                  <TableCell className="tnum text-right font-mono text-[11.5px]">
                    {formatPrice(t.lastPrice, t.quoteDecimals, 6)} {t.quoteSymbol ?? ''}
                  </TableCell>
                  <TableCell className="tnum text-right font-mono text-[11.5px]">
                    {t.tradeCount}
                  </TableCell>
                  <TableCell className="tnum text-right font-mono text-[11.5px]">
                    {t.volAllUsd != null ? (
                      `$${new Intl.NumberFormat('en-US', {
                        notation: 'compact',
                        maximumFractionDigits: 1,
                      }).format(t.volAllUsd)}`
                    ) : (
                      <span className="text-mut2">—</span>
                    )}
                  </TableCell>

                  <TableCell>
                    {t.hidden ? (
                      <Badge className="h-5 bg-warn/15 text-[10.5px] text-warn hover:bg-warn/15">
                        已下架
                      </Badge>
                    ) : (
                      <span className="text-[11.5px] text-mut">
                        {t.graduated ? '已毕业' : '交易中'}
                      </span>
                    )}
                  </TableCell>

                  <TableCell className="tnum font-mono text-[11px] text-mut2">
                    {formatDateTime(t.createdTime)}
                  </TableCell>

                  <TableCell className="pr-6">
                    <div className="flex justify-end">
                      <TokenActions token={t} compact />
                    </div>
                  </TableCell>
                </TableRow>
              )
            })}
          </TableBody>
        </Table>
      </TableCard>

      <Pager page={page} pageCount={data?.pageCount ?? 1} onChange={setPage} />
    </Page>
  )
}
